import { useState, useEffect } from 'react'
import { FiMail, FiCheckCircle, FiAlertCircle, FiEye, FiEyeOff, FiX, FiSettings, FiActivity } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { formatearFechaHora } from '../utils/fechas'
import { PageHeader } from '../ui'

// Motivos que devuelve sendEmail, en lenguaje entendible desde el panel.
// Cualquier otro valor es el mensaje literal del proveedor y se muestra tal cual.
const MOTIVOS = {
  brevo_not_configured: 'Brevo sin configurar',
  email_disabled: 'Emails desactivados',
  template_disabled: 'Plantilla desactivada',
  template_not_found: 'Plantilla no encontrada'
}

const motivoLegible = (motivo) => {
  if (!motivo) return 'Error'
  return MOTIVOS[motivo] || (motivo.length > 60 ? motivo.slice(0, 60) + '…' : motivo)
}

const GestionSistema = () => {
  const [emailConfig, setEmailConfig] = useState({ email_from: '', app_url: '', brevo_api_key_set: false, brevo_api_key_masked: '' })
  const [emailForm, setEmailForm] = useState({ brevo_api_key: '', email_from: '', app_url: '', email_test: '' })
  const [loadingEmail, setLoadingEmail] = useState(false)
  const [testingEmail, setTestingEmail] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)

  const [emailTemplates, setEmailTemplates] = useState([])
  const [editingTpl, setEditingTpl] = useState(null)
  const [tplForm, setTplForm] = useState({ subject: '', body_html: '' })
  const [previewHtml, setPreviewHtml] = useState('')
  const [tplTestEmail, setTplTestEmail] = useState('')
  const [tplLoading, setTplLoading] = useState(false)

  const [emailLogs, setEmailLogs] = useState([])
  const [logsResumen, setLogsResumen] = useState({ total: 0, fallidos: 0 })
  const [soloFallos, setSoloFallos] = useState(false)
  const [loadingLogs, setLoadingLogs] = useState(false)

  const token = () => localStorage.getItem('token')
  const headers = () => ({ 'Authorization': `Bearer ${token()}` })
  const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` })

  useEffect(() => { cargarEmailConfig(); cargarTemplates(); cargarLogs(false) }, [])

  const cargarLogs = async (fallos = soloFallos) => {
    setLoadingLogs(true)
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/logs?limit=50${fallos ? '&solo_fallos=1' : ''}`, { headers: headers() })
      if (res.ok) {
        const data = await res.json()
        setEmailLogs(data.logs || [])
        setLogsResumen(data.ultimas_24h || { total: 0, fallidos: 0 })
      }
    } catch {}
    finally { setLoadingLogs(false) }
  }

  const cargarEmailConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/config`, { headers: headers() })
      if (res.ok) {
        const data = await res.json()
        setEmailConfig(data)
        setEmailForm(f => ({ ...f, email_from: data.email_from || '', app_url: data.app_url || '' }))
      }
    } catch {}
  }

  const guardarEmailConfig = async () => {
    setLoadingEmail(true)
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/config`, {
        method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(emailForm)
      })
      if (res.ok) {
        toast.success('Configuración guardada')
        setEmailForm(f => ({ ...f, brevo_api_key: '' }))
        setShowApiKey(false)
        cargarEmailConfig()
      } else { const d = await res.json(); toast.error(d.error || 'Error al guardar') }
    } catch { toast.error('Error al guardar') }
    finally { setLoadingEmail(false) }
  }

  // email_enabled solo desactiva si vale '0'/'false'; si la clave no existe se
  // considera activo (mismo criterio que el servicio de email del backend).
  const emailsActivos = !['0', 'false'].includes(String(emailConfig.email_enabled ?? '1').toLowerCase())

  const toggleEmailEnabled = async () => {
    const nuevo = !emailsActivos
    setLoadingEmail(true)
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/config`, {
        method: 'PUT', headers: jsonHeaders(), body: JSON.stringify({ email_enabled: nuevo })
      })
      if (res.ok) {
        toast.success(nuevo ? 'Envío de emails activado' : 'Envío de emails desactivado')
        cargarEmailConfig()
      } else { const d = await res.json(); toast.error(d.error || 'Error al guardar') }
    } catch { toast.error('Error al guardar') }
    finally { setLoadingEmail(false) }
  }

  const enviarEmailPrueba = async () => {
    if (!emailForm.email_test) { toast.error('Introduce un email de destino'); return }
    setTestingEmail(true)
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/test`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ email_destino: emailForm.email_test })
      })
      const data = await res.json()
      if (res.ok) toast.success('Email de prueba enviado')
      else toast.error(data.error || 'Error al enviar')
      cargarLogs()
    } catch { toast.error('Error al enviar') }
    finally { setTestingEmail(false) }
  }

  const cargarTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/templates`, { headers: headers() })
      if (res.ok) setEmailTemplates(await res.json())
    } catch {}
  }

  const toggleTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/templates/${templateId}/toggle`, {
        method: 'PATCH', headers: headers()
      })
      if (res.ok) {
        const { activo } = await res.json()
        setEmailTemplates(prev => prev.map(t => t.template_id === templateId ? { ...t, activo } : t))
      }
    } catch { toast.error('Error al cambiar estado') }
  }

  const guardarTemplate = async () => {
    if (!editingTpl) return
    setTplLoading(true)
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/templates/${editingTpl.template_id}`, {
        method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(tplForm)
      })
      if (res.ok) { toast.success('Plantilla guardada'); cargarTemplates() }
      else { const d = await res.json(); toast.error(d.error) }
    } catch { toast.error('Error al guardar') }
    finally { setTplLoading(false) }
  }

  const previewTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/templates/${templateId}/preview`, {
        method: 'POST', headers: jsonHeaders()
      })
      if (res.ok) { const d = await res.json(); setPreviewHtml(d.html) }
    } catch {}
  }

  const testTemplate = async (templateId) => {
    if (!tplTestEmail) { toast.error('Introduce un email'); return }
    try {
      const res = await fetch(`${API_URL}/configuracion-global/email/templates/${templateId}/test`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ email_destino: tplTestEmail })
      })
      const d = await res.json()
      if (res.ok) toast.success('Email de prueba enviado')
      else toast.error(d.error || 'Error')
    } catch { toast.error('Error al enviar') }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ X1"
        kicker="Sistema"
        title="Configuración"
        italic="global."
        sub="Email, plantillas, integraciones y mantenimiento de la plataforma."
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Email / Brevo */}
      <div className="ui-card p-5">
        <div className="flex items-center gap-2 mb-5">
          <FiMail className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-[var(--ink)]">Servicio de Email (Brevo)</h3>
          {emailConfig.brevo_api_key_set
            ? <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full"><FiCheckCircle className="h-3 w-3" /> Configurado</span>
            : <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full"><FiAlertCircle className="h-3 w-3" /> Sin configurar</span>
          }
        </div>
        <p className="text-sm text-[var(--ink-3)] mb-5">
          Necesario para emails transaccionales (verificación, trial, pagos, notificaciones).
          Obtén tu API key en <a href="https://app.brevo.com/settings/keys/smtp" target="_blank" rel="noreferrer" className="text-blue-600 underline">Brevo SMTP &amp; API</a>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="md:col-span-2">
            <label className="label">Brevo API Key</label>
            <div className="relative">
              <input type={showApiKey ? 'text' : 'password'} value={emailForm.brevo_api_key}
                onChange={e => setEmailForm(f => ({ ...f, brevo_api_key: e.target.value }))}
                placeholder={emailConfig.brevo_api_key_set ? emailConfig.brevo_api_key_masked + ' (dejar vacío para no cambiar)' : 'xkeysib-xxxxxxxx'}
                className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white pr-10" />
              <button type="button" onClick={() => setShowApiKey(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)] hover:text-[var(--ink-2)]">
                {showApiKey ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">Dirección remitente</label>
            <input type="text" value={emailForm.email_from} onChange={e => setEmailForm(f => ({ ...f, email_from: e.target.value }))}
              placeholder="PresenzIA <hola@presenzia.app>"
              className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
            <p className="text-xs text-[var(--ink-4)] mt-1">El dominio debe estar autenticado en Brevo</p>
          </div>
          <div>
            <label className="label">URL de la aplicación</label>
            <input type="text" value={emailForm.app_url} onChange={e => setEmailForm(f => ({ ...f, app_url: e.target.value }))}
              placeholder="https://app.presenzia.app"
              className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
            <p className="text-xs text-[var(--ink-4)] mt-1">Se usa en los enlaces de los emails</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4 p-3 rounded-lg bg-[var(--bg-2)] border border-[var(--line)]">
          <div>
            <p className="text-sm font-medium text-[var(--ink)]">Envío de emails</p>
            <p className="text-xs text-[var(--ink-4)]">
              Si lo desactivas, la aplicación deja de enviar cualquier email transaccional.
            </p>
          </div>
          <button onClick={toggleEmailEnabled} disabled={loadingEmail}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${emailsActivos ? 'bg-green-500' : 'bg-[var(--line-2)]'}`}
            title={emailsActivos ? 'Desactivar envíos' : 'Activar envíos'}>
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${emailsActivos ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </button>
        </div>

        <div className="flex flex-wrap gap-3 pt-2 border-t border-[var(--line)]">
          <button onClick={guardarEmailConfig} disabled={loadingEmail} className="btn btn-primary btn-ripple">
            {loadingEmail ? 'Guardando...' : 'Guardar configuración'}
          </button>
          <div className="flex gap-2 ml-auto">
            <input type="email" value={emailForm.email_test} onChange={e => setEmailForm(f => ({ ...f, email_test: e.target.value }))}
              placeholder="email@prueba.com"
              className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
            <button onClick={enviarEmailPrueba} disabled={testingEmail || !emailConfig.brevo_api_key_set}
              className="btn btn-secondary" title={!emailConfig.brevo_api_key_set ? 'Guarda la API key primero' : ''}>
              {testingEmail ? 'Enviando...' : 'Enviar prueba'}
            </button>
          </div>
        </div>
      </div>

      {/* Historial de envíos */}
      <div className="ui-card p-5">
        <div className="flex items-center gap-2 mb-5">
          <FiActivity className="h-5 w-5 text-emerald-500" />
          <h3 className="text-lg font-semibold text-[var(--ink)]">Historial de envíos</h3>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => { const v = !soloFallos; setSoloFallos(v); cargarLogs(v) }}
              className={`text-xs px-2 py-1 rounded-full border transition-colors ${soloFallos ? 'bg-red-50 text-red-600 border-red-200' : 'text-[var(--ink-3)] border-[var(--line-2)] hover:bg-[var(--bg-2)]'}`}>
              Solo fallos
            </button>
            <button onClick={() => cargarLogs()} disabled={loadingLogs}
              className="text-xs text-[var(--ink-3)] hover:text-blue-600 px-2 py-1 rounded hover:bg-blue-50 transition-colors" title="Actualizar">
              {loadingLogs ? 'Cargando...' : 'Actualizar'}
            </button>
          </div>
        </div>

        {logsResumen.fallidos > 0 ? (
          <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <FiAlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">
              <strong>{logsResumen.fallidos}</strong> de {logsResumen.total} envíos han fallado en las últimas 24 h.
              Revisa la API key, el remitente y las <a href="https://app.brevo.com/security/authorised_ips" target="_blank" rel="noreferrer" className="underline">IPs autorizadas en Brevo</a>.
            </p>
          </div>
        ) : (
          <p className="text-sm text-[var(--ink-3)] mb-4">
            {logsResumen.total > 0
              ? `${logsResumen.total} envíos en las últimas 24 h, todos correctos.`
              : 'Sin envíos en las últimas 24 h.'}
          </p>
        )}

        {emailLogs.length === 0 ? (
          <p className="text-sm text-[var(--ink-4)] py-4 text-center">
            {soloFallos ? 'Ningún envío fallido registrado.' : 'Todavía no hay envíos registrados.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--ink-4)] border-b border-[var(--line)]">
                  <th className="py-2 pr-3 font-medium">Fecha</th>
                  <th className="py-2 pr-3 font-medium">Destinatario</th>
                  <th className="py-2 pr-3 font-medium">Plantilla</th>
                  <th className="py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {emailLogs.map(log => (
                  <tr key={log.id} className="border-b border-[var(--line)] last:border-0">
                    <td className="py-2 pr-3 text-[var(--ink-3)] whitespace-nowrap">{formatearFechaHora(log.created_at_ms)}</td>
                    <td className="py-2 pr-3 text-[var(--ink-2)] truncate max-w-[220px]" title={log.destinatario}>{log.destinatario}</td>
                    <td className="py-2 pr-3 text-[var(--ink-3)]">{log.template_id}</td>
                    <td className="py-2">
                      {log.exito ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                          <FiCheckCircle className="h-3 w-3" /> Enviado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full" title={log.motivo || ''}>
                          <FiAlertCircle className="h-3 w-3" /> {motivoLegible(log.motivo)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Plantillas de email */}
      <div className="ui-card p-5">
        <div className="flex items-center gap-2 mb-5">
          <FiMail className="h-5 w-5 text-purple-500" />
          <h3 className="text-lg font-semibold text-[var(--ink)]">Plantillas de Email</h3>
          <span className="text-xs text-[var(--ink-4)] ml-auto">{emailTemplates.filter(t => t.activo).length}/{emailTemplates.length} activas</span>
        </div>

        <div className="space-y-2">
          {emailTemplates.map(tpl => (
            <div key={tpl.template_id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${tpl.activo ? 'border-[var(--line)] bg-white' : 'border-[var(--line)] bg-[var(--bg-2)] opacity-60'}`}>
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--ink)]">{tpl.nombre}</span>
                  {tpl.body_html && <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">Personalizado</span>}
                </div>
                <p className="text-xs text-[var(--ink-4)] truncate">{tpl.descripcion}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { previewTemplate(tpl.template_id) }}
                  className="text-xs text-[var(--ink-3)] hover:text-blue-600 px-2 py-1 rounded hover:bg-blue-50 transition-colors">
                  Vista previa
                </button>
                <button onClick={() => { setEditingTpl(tpl); setTplForm({ subject: tpl.subject || '', body_html: tpl.body_html || '' }); setPreviewHtml('') }}
                  className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors font-medium">
                  Editar
                </button>
                <button onClick={() => toggleTemplate(tpl.template_id)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${tpl.activo ? 'bg-green-500' : 'bg-[var(--line-2)]'}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${tpl.activo ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal preview */}
      {previewHtml && !editingTpl && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setPreviewHtml('')}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="text-sm font-bold text-[var(--ink)]">Vista previa del email</h3>
              <button onClick={() => setPreviewHtml('')} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-auto bg-[var(--bg-2)] p-4">
              <iframe srcDoc={previewHtml} title="Preview" className="w-full bg-white rounded-lg shadow" style={{ height: '600px', border: 'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal editar plantilla */}
      {editingTpl && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setEditingTpl(null); setPreviewHtml('') }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-base font-bold text-[var(--ink)]">Editar: {editingTpl.nombre}</h3>
                <p className="text-xs text-[var(--ink-4)]">{editingTpl.descripcion}</p>
              </div>
              <button onClick={() => { setEditingTpl(null); setPreviewHtml('') }} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX className="h-5 w-5" /></button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-5">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Asunto</label>
                    <input type="text" value={tplForm.subject} onChange={e => setTplForm({ ...tplForm, subject: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder="Dejar vacío = asunto por defecto" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--ink-2)] mb-1 block">Contenido HTML</label>
                    <textarea value={tplForm.body_html} onChange={e => setTplForm({ ...tplForm, body_html: e.target.value })}
                      rows={14}
                      className="w-full px-3 py-2 text-xs font-mono border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
                      placeholder="Dejar vacío = contenido por defecto. Usa HTML + variables {{variable}}." />
                  </div>
                  <div>
                    <p className="text-xs text-[var(--ink-3)] mb-1.5">Variables disponibles:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {JSON.parse(editingTpl.variables || '[]').map(v => (
                        <button key={v} type="button"
                          onClick={() => setTplForm(f => ({ ...f, body_html: f.body_html + `{{${v}}}` }))}
                          className="text-[11px] px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 font-mono transition-colors">
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-[var(--ink-2)]">Vista previa</p>
                    <button onClick={() => previewTemplate(editingTpl.template_id)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium">Actualizar</button>
                  </div>
                  <div className="bg-[var(--bg-2)] rounded-lg overflow-hidden border border-[var(--line)]" style={{ height: '400px' }}>
                    {previewHtml
                      ? <iframe srcDoc={previewHtml} title="Preview" className="w-full h-full" style={{ border: 'none' }} />
                      : <div className="flex items-center justify-center h-full text-xs text-[var(--ink-4)]">Pulsa "Actualizar" para ver la vista previa</div>
                    }
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input type="email" value={tplTestEmail} onChange={e => setTplTestEmail(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      placeholder="Email de prueba" />
                    <button onClick={() => testTemplate(editingTpl.template_id)}
                      className="btn btn-secondary text-xs">Enviar prueba</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t bg-[var(--bg-2)]">
              <button onClick={() => { setTplForm({ subject: '', body_html: '' }) }}
                className="text-xs text-[var(--ink-3)] hover:text-[var(--ink-2)]">Restaurar por defecto</button>
              <div className="flex gap-3">
                <button onClick={() => { setEditingTpl(null); setPreviewHtml('') }} className="btn btn-secondary">Cancelar</button>
                <button onClick={guardarTemplate} disabled={tplLoading} className="btn btn-primary btn-ripple">
                  {tplLoading ? 'Guardando...' : 'Guardar plantilla'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionSistema
